import { defineNuxtPlugin } from 'nuxt/app';
import type { QueryClient } from '@tanstack/vue-query';
import { isBundledNativeOrigin, isNativeApp } from '~/functions/appOrigin';
import { consumePendingStripeReturn } from '~/functions/openCheckoutUrl';
import useAuthStore from '~/stores/auth';

/**
 * Bringing the packaged app back up to date after a Stripe page.
 *
 * On the web a purchase ends with a page load: Stripe redirects to `/payment/success`, the
 * app boots from scratch and every piece of state is fresh by construction. In the packaged
 * app none of that happens. The WebView never moved — Stripe was shown in a browser on top
 * of it — so when the user comes back, the app is exactly as they left it and still believes
 * they own nothing. The purchase itself is safe (the Stripe webhook grants the entitlement
 * server-side), but nothing on screen reflects it.
 *
 * This is the floor of the return trip, and it works with no API change, no Universal Links
 * and no Apple entitlements. The deep-link return that routes the user straight to
 * `/payment/success` in the app is a separate, later piece; when it lands this stays as the
 * fallback for every path that misses it. See docs/mobile-app-status.md §7.
 *
 * ## Why two events
 *
 * Which one fires depends on how the Stripe page was shown, and that is not entirely ours to
 * decide:
 *
 *   - **`browserFinished`** — the user dismissed the in-app browser (SFSafariViewController
 *     on iOS, a Custom Tab on Android) that `openCheckoutUrl` presented. The app was never
 *     backgrounded, because the sheet sits on top of it, so this is the only signal in the
 *     ordinary case.
 *   - **`appStateChange` → active** — the app genuinely returned to the foreground. That
 *     happens when the page opened in a *separate* browser app instead, which is what
 *     Capacitor's own external-URL handling does, and what any link we did not open
 *     ourselves will still do.
 *
 * Both are cheap, both are guarded by `consumePendingStripeReturn`, and the first to arrive
 * consumes the flag — so an ordinary app switch with no purchase behind it costs nothing,
 * and a purchase never refreshes twice.
 *
 * ## Why invalidate everything
 *
 * A purchase changes more than one query: subscriptions, per-course access flags, bundle
 * contents and the profile can all move at once. `usePortalSwitcher` takes the same blanket
 * approach for the same reason, and Vue Query only refetches queries that are actually
 * mounted. `setCurrentUserSubscriptions` is called alongside it because the auth store keeps
 * its own copy that predates the Vue Query migration and is what the middleware refreshes.
 *
 * ## Why `force`
 *
 * `setCurrentUserSubscriptions()` is load-once: it returns early if any of its four arrays is
 * non-empty, and `purchasable` is populated for essentially every signed-in user. So this
 * call did nothing at all until `force` existed — the flow worked on `invalidateQueries()`
 * alone, and the auth store's copy stayed as stale as it was before the purchase.
 *
 * Forcing is safe here *because of the flag*, not despite it. `consumePendingStripeReturn()`
 * means this runs once after a checkout, never on an ordinary app switch — which matters,
 * since `user-subscriptions` verifies against the payment provider on every call. Do not
 * reuse this listener for a general foreground refresh without a debounce.
 */
export default defineNuxtPlugin((nuxtApp) => {
   if (!isNativeApp() || !isBundledNativeOrigin()) return;

   const refreshAfterStripeReturn = async () => {
      if (!consumePendingStripeReturn()) return;

      // Read lazily: this plugin sorts before `vue-query.ts`, so the client does not exist
      // yet at setup time — but it does by the time a listener fires.
      const queryClient = nuxtApp.$queryClient as QueryClient | undefined;

      queryClient?.invalidateQueries();

      const authStore = useAuthStore();

      if (!authStore.accessToken) return;

      await authStore.setCurrentUserSubscriptions({ force: true });
   };

   void (async () => {
      const [{ App }, { Browser }] = await Promise.all([
         import('@capacitor/app'),
         import('@capacitor/browser'),
      ]);

      await Browser.addListener('browserFinished', () => {
         void refreshAfterStripeReturn();
      });

      await App.addListener('appStateChange', ({ isActive }) => {
         if (!isActive) return;

         void refreshAfterStripeReturn();
      });
   })();
});
